/**
 * Zoom-aware layout-label sizing and priority (sharp-labels WP-B).
 *
 * WHY: labels used to render at a fixed 11 px screen size at every zoom, so a
 * 13 MWp site at whole-site zoom is a solid carpet of text (owner screenshot,
 * 2026-08-26). Three mechanisms combine:
 *
 *   1. LEVEL LADDER — macro levels (station/inverter/dccb) reveal first;
 *      strings arrive at working zoom; modules only near.
 *   2. LEGIBLE-OR-ABSENT — a label renders at ≥ BASE_SIZE px or not at all.
 *      The first iteration interpolated each level's size up FROM ZERO, and
 *      the owner's smoke showed why that fails: a whole zoom band renders
 *      thousands of 3–6 px specks — too small to read AND too small to
 *      collide, so the carpet survives as noise. Reveals are STEPS.
 *   3. COLLISION PRIORITY — with text-allow-overlap off, symbol-sort-key
 *      decides who wins a collision: macro levels beat micro levels.
 *
 * The REVEAL table is the single source of truth. The Mapbox step expression
 * and the plain-JS evaluator are both derived from it — the JS twin exists
 * for the drag ghost, whose DOM stand-ins must match the rendered size at
 * the current zoom.
 */

/** Size (px) a level renders at the moment it reveals. Never smaller. */
const BASE_SIZE = 10;
/** Growth per zoom level past reveal, capped at MAX_SIZE. */
const GROWTH_PER_ZOOM = 1.1;
const MAX_SIZE = 16;

/** Zoom at which each level becomes visible (at BASE_SIZE, legible). Macro
 *  levels first; micro levels only when the ground can afford them. Covers
 *  every classification level seen in real layouts (IL-MAH surfaces
 *  mppt/row/infrastructure/unclassified; string sheets add the rest). */
const LEVEL_REVEAL: Record<string, number> = {
  station: 13,
  inverter: 14,
  dccb: 14.5,
  mppt: 15,
  zone: 15,
  infrastructure: 15.5,
  row: 17,
  string: 17.5,
  unclassified: 18,
  module: 19.5,
};
/** Unknown / custom levels behave like strings: working-zoom reveal. */
const DEFAULT_REVEAL = 17.5;

/** The zoom at which MODULE-scale detail exists at all: module labels, the
 *  grid rectangles, and the per-module numbers appear TOGETHER here (owner,
 *  2026-08-26: rectangles must not outlive their numbers by four zoom
 *  levels). Derived from the module reveal so the two cannot drift. */
export const MODULE_DETAIL_MINZOOM = LEVEL_REVEAL.module;

/** Derived string lines (WP-C) appear with the string labels. */
export const STRING_LINE_MINZOOM = LEVEL_REVEAL.string;

/** Collision priority — LOWER wins. Macro levels beat micro levels. */
const LEVEL_PRIORITY: Record<string, number> = {
  station: 0,
  inverter: 1,
  dccb: 2,
  mppt: 3,
  zone: 4,
  infrastructure: 5,
  row: 6,
  string: 7,
  unclassified: 8,
  module: 9,
};
const DEFAULT_PRIORITY = 8;

/** Step bands, every 0.5 zoom. Size only changes at band edges (a ≤1 px pop,
 *  imperceptible) — bands keep the expression small and the JS twin exact. */
const BAND_START = 13;
const BAND_END = 21.5;
const BAND_STEP = 0.5;

function sizeFor(level: string | undefined, zoom: number): number {
  const reveal = LEVEL_REVEAL[level ?? ''] ?? DEFAULT_REVEAL;
  if (zoom < reveal) return 0;
  return Math.min(MAX_SIZE, BASE_SIZE + (zoom - reveal) * GROWTH_PER_ZOOM);
}

function bandZooms(): number[] {
  const zooms: number[] = [];
  for (let z = BAND_START; z <= BAND_END + 1e-9; z += BAND_STEP) zooms.push(z);
  return zooms;
}

function sizeMatchExpr(zoom: number): unknown[] {
  const expr: unknown[] = ['match', ['get', 'level']];
  for (const level of Object.keys(LEVEL_REVEAL)) {
    expr.push(level, sizeFor(level, zoom));
  }
  expr.push(sizeFor(undefined, zoom));
  return expr;
}

/** Step zoom+data expression for the labels layer's `text-size`:
 *  ['step', ['zoom'], <below-first-band>, z1, <band1>, z2, <band2>, ...] */
export function buildLabelTextSizeExpr(): unknown[] {
  const zooms = bandZooms();
  const expr: unknown[] = ['step', ['zoom'], sizeMatchExpr(zooms[0])];
  for (let i = 1; i < zooms.length; i++) {
    expr.push(zooms[i], sizeMatchExpr(zooms[i]));
  }
  return expr;
}

/** Data expression for the labels layer's `symbol-sort-key`. */
export function buildLabelSortKeyExpr(): unknown[] {
  const expr: unknown[] = ['match', ['get', 'level']];
  for (const [level, priority] of Object.entries(LEVEL_PRIORITY)) {
    expr.push(level, priority);
  }
  expr.push(DEFAULT_PRIORITY);
  return expr;
}

/** Plain-JS twin of buildLabelTextSizeExpr — same bands, same step
 *  semantics Mapbox applies (value of the band the zoom falls in). Used by
 *  the drag ghost's DOM stand-ins. */
export function labelSizeAt(zoom: number, level: string | undefined): number {
  const zooms = bandZooms();
  let band = zooms[0];
  for (const z of zooms) {
    if (zoom >= z) band = z;
    else break;
  }
  return sizeFor(level, band);
}
